import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CommunityStatRow {
  room_id: string;
  visit_count: number;
  total_time_seconds: number;
}

export interface CommunityAchievementRow {
  achievement_id: string;
  unlock_count: number;
  first_unlocked_at: string;
  last_unlocked_at: string;
}

const fetchCommunityStats = async (): Promise<CommunityStatRow[]> => {
  const { data, error } = await supabase
    .from('community_stats')
    .select('room_id, visit_count, total_time_seconds')
    .order('visit_count', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data as any) ?? [];
};

const fetchCommunityAchievements = async (): Promise<CommunityAchievementRow[]> => {
  const { data, error } = await supabase
    .from('community_achievements')
    .select('achievement_id, unlock_count, first_unlocked_at, last_unlocked_at')
    .order('unlock_count', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data as any) ?? [];
};

export const useCommunityStats = () => {
  const stats = useQuery({
    queryKey: ['community-stats'],
    queryFn: fetchCommunityStats,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  const achievements = useQuery({
    queryKey: ['community-achievements'],
    queryFn: fetchCommunityAchievements,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  const totalVisits = stats.data?.reduce((sum, s) => sum + s.visit_count, 0) ?? 0;
  const totalTimeSeconds = stats.data?.reduce((sum, s) => sum + s.total_time_seconds, 0) ?? 0;

  return {
    stats: stats.data ?? [],
    achievements: achievements.data ?? [],
    totalVisits,
    totalTimeSeconds,
    isLoading: stats.isLoading || achievements.isLoading,
  };
};

/** Fire-and-forget report to edge function */
export const reportRoomVisit = (roomId: string, timeSeconds: number) => {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/community-stats`;
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
    body: JSON.stringify({ action: 'record-visit', room_id: roomId, time_seconds: timeSeconds }),
  }).catch(() => {});
};

export const reportAchievementUnlock = (achievementId: string) => {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/community-stats`;
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
    body: JSON.stringify({ action: 'record-achievement', achievement_id: achievementId }),
  }).catch(() => {});
};
